import { useEffect, useState } from 'react'
import { SEO } from './components/SEO'
import { LandingPage } from './components/LandingPage'
import { PrivacyPolicy, TermsOfService } from './components/LegalPage'

const APP_NAME = '10K Books'
const TAGLINE = '10K Books. 10 Minutes a Day.'
const DESCRIPTION =
  "Read or listen to the key ideas from the world's best books — beautifully, offline, in English, French, or Spanish."
const APP_URL = 'https://10000-offline-books-in-10-min.altuzar.dev/'
const WEB_APP_URL = 'https://app.10000-offline-books-in-10-min.altuzar.dev/'

type Route = 'home' | 'privacy' | 'terms'

/** Map a hash/path (e.g. `#/privacy`, `/privacy/`) to a known route. */
function routeFromPath(path: string | undefined): Route {
  if (!path) return 'home'
  // strip leading `#`, `/`, and trailing `/`
  const normalized = path.replace(/^#/, '').replace(/^\/+/, '').replace(/\/+$/, '')
  if (normalized === 'privacy') return 'privacy'
  if (normalized === 'terms') return 'terms'
  return 'home'
}

interface AppProps {
  /** Used for SSR/prerender to render a deterministic route without `window`. */
  initialPath?: string
}

function App({ initialPath }: AppProps) {
  const mode = import.meta.env.VITE_MODE || 'launch'
  const appName = import.meta.env.VITE_APP_NAME || APP_NAME
  const description = import.meta.env.VITE_APP_DESCRIPTION || DESCRIPTION

  const getClientRoute = (): Route =>
    typeof window !== 'undefined' ? routeFromPath(window.location.hash) : routeFromPath(initialPath)

  const [route, setRoute] = useState<Route>(getClientRoute)

  useEffect(() => {
    const onHashChange = () => {
      setRoute(routeFromPath(window.location.hash))
    }
    // Sync once on mount in case hydration started from a different hash.
    onHashChange()
    window.addEventListener('hashchange', onHashChange)
    return () => window.removeEventListener('hashchange', onHashChange)
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const sectionId = window.location.hash.replace(/^#/, '')
    if (route === 'home' && sectionId && !sectionId.startsWith('/')) {
      window.requestAnimationFrame(() => {
        document.getElementById(sectionId)?.scrollIntoView()
      })
      return
    }

    window.scrollTo(0, 0)
  }, [route])

  if (route === 'privacy') {
    return (
      <>
        <SEO
          title={`Privacy Policy — ${appName}`}
          description={`How ${appName} collects, uses, and protects your information.`}
          url={`${APP_URL}privacy/`}
          type="article"
        />
        <PrivacyPolicy appName={appName} />
      </>
    )
  }

  if (route === 'terms') {
    return (
      <>
        <SEO
          title={`Terms of Service — ${appName}`}
          description={`The terms that govern your use of ${appName}.`}
          url={`${APP_URL}terms/`}
          type="article"
        />
        <TermsOfService appName={appName} />
      </>
    )
  }

  return (
    <>
      <SEO
        title={appName}
        description={description}
        image={import.meta.env.VITE_OG_IMAGE || '/og-20260822.png'}
        url={import.meta.env.VITE_APP_URL || APP_URL}
      />

      <LandingPage
        mode={mode === 'waitlist' ? 'waitlist' : 'launch'}
        appName={appName}
        tagline={TAGLINE}
        description={description}
        iosUrl={import.meta.env.VITE_IOS_APP_URL}
        androidUrl={import.meta.env.VITE_ANDROID_APP_URL}
        externalTestingEndpoint={import.meta.env.VITE_EXTERNAL_TESTING_API_ENDPOINT}
        webUrl={import.meta.env.VITE_WEB_APP_URL || WEB_APP_URL}
        screenshotUrl={import.meta.env.VITE_APP_SCREENSHOT_URL}
      />
    </>
  )
}

export default App
